import { CLASS_COLORS } from "./classColors";
let DB_NAME = "ImageLabelingDB";
const STORE_NAME = "images";
const CLASS_STORE = "classes";
const PROJECT_STORE = "projects";
const ANNOTATION_STORE = "annotations";
const MASTER_RECT_STORE = "master_rectangles";
const SEGMENT_STORE = "segments";

export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 4); // Increment version to trigger upgrade
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(CLASS_STORE)) {
        db.createObjectStore(CLASS_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(PROJECT_STORE)) {
        db.createObjectStore(PROJECT_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(ANNOTATION_STORE)) {
        db.createObjectStore(ANNOTATION_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(MASTER_RECT_STORE)) {
        db.createObjectStore(MASTER_RECT_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(SEGMENT_STORE)) {
        db.createObjectStore(SEGMENT_STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const saveImages = async (images, zipName, user) => {
  const db = await initDB();
  const tx = db.transaction([STORE_NAME, PROJECT_STORE], "readwrite");

  // Clear old images
  await tx.objectStore(STORE_NAME).clear();

  // Save new images
  images.forEach((image) => {
    tx.objectStore(STORE_NAME).put({
      id: image.id,
      name: image.name,
      image: image.blob,
    });
  });

  // Save ZIP/project name
  tx.objectStore(PROJECT_STORE).put({
    id: 1,
    name: zipName,
    user: user,
    updatedAt: new Date().toISOString(),
  });

  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
  });
};
const MAX_CACHED_IMAGES = 60; // Keep 60 images in cache
const CACHE_MARGIN = 20; // Load 20 images beyond current window

export const updateImageCache = async (
  newImages,
  currentIndex,
  projectName
) => {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);

  const firstKey = `${projectName}_firstLoaded`;
  const lastKey = `${projectName}_lastLoaded`;
  let cacheStart = Math.max(0, currentIndex - CACHE_MARGIN);
  let cacheEnd = currentIndex + CACHE_MARGIN;
  if (cacheStart > cacheEnd) {
    [cacheStart, cacheEnd] = [cacheEnd, cacheStart];
  }
  // Always initialize as array
  let existingImages = [];
  // console.log("newImages", newImages);
  try {
    // Get all images in current cache window
  
    const rangeQuery = IDBKeyRange.bound(cacheStart, cacheEnd);
    const request = store.getAll(rangeQuery);
    existingImages = await new Promise((resolve) => {
      request.onsuccess = () => {
        const items = request.result;
        resolve(
          items.map((item) => ({
            id: item.id,
            name: item.name,
            data: URL.createObjectURL(item.image),
            blob: item.image,
          }))
        );
      };
    });
    // console.log("existing Images", existingImages);
    // Filter new images to add
    const existingIds = new Set(existingImages.map((img) => img.id));
    // console.log("existingIds", existingIds);
    // console.log("cacheStart, cacheEnd", cacheStart, cacheEnd);
    const toAdd = newImages.filter(img => 
      !existingIds.has(img.id) && 
      img.id >= cacheStart && 
      img.id <= cacheEnd
    );

    // Remove old images outside window - SAFE VERSION
    if (cacheStart > 0) {
      const removeQuery = IDBKeyRange.upperBound(cacheStart - 1);
      await store.delete(removeQuery);
    }

    if (cacheEnd < Number.MAX_SAFE_INTEGER) {
      const upperRemoveQuery = IDBKeyRange.lowerBound(cacheEnd + 1);
      await store.delete(upperRemoveQuery);
    }
    // console.log("toAdd", toAdd);
    // Add new images
    await Promise.all(
      toAdd.map((img) =>
        store.put({
          id: img.id,
          name: img.name,
          image: img.blob,
        })
      )
    );

    // Update range markers
    const allIds = [
      ...existingImages.map((img) => img.id),
      ...toAdd.map((img) => img.id),
    ];
    if (allIds.length > 0) {
      const newFirst = Math.min(...allIds);
     
      const newLast = Math.max(...allIds);
      // console.log('newFirst and newLast', newFirst, newLast)
      localStorage.setItem(firstKey, newFirst);
      localStorage.setItem(lastKey, newLast);
    }
  } catch (error) {
    console.error("Cache update failed:", error);
    throw error;
  }
};

export const getImages = async () => {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const request = store.getAll();

  return new Promise((resolve) => {
    request.onsuccess = () => {
      const items = request.result;
      resolve(
        items.map((item) => ({
          id: item.id,
          name: item.name,
          data: URL.createObjectURL(item.image),
          blob: item.image,
        }))
      );
    };
  });
};

export const addMoreImages = async (newImages) => {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);

  // Get current count to determine starting ID
  const countRequest = store.count();

  return new Promise((resolve, reject) => {
    countRequest.onsuccess = () => {
      const currentCount = countRequest.result;
      let successCount = 0;

      newImages.forEach((image, index) => {
        const request = store.put({
          id: currentCount + index,
          name: image.name,
          image: image.blob, // Store the Blob directly
        });

        request.onsuccess = () => {
          successCount++;
          if (successCount === newImages.length) {
            resolve();
          }
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    };

    countRequest.onerror = () => {
      reject(countRequest.error);
    };
  });
};

export const getProjectName = async () => {
  const db = await initDB();
  const tx = db.transaction(PROJECT_STORE, "readonly");
  const store = tx.objectStore(PROJECT_STORE);
  const request = store.get(1);

  return new Promise((resolve) => {
    request.onsuccess = () => {
      resolve(request.result?.name || "");
    };
  });
};

export const getDBUserName = async () => {
  const db = await initDB();
  const tx = db.transaction(PROJECT_STORE, "readonly");
  const store = tx.objectStore(PROJECT_STORE);
  const request = store.get(1);

  return new Promise((resolve) => {
    request.onsuccess = () => {
      resolve(request.result?.user || "");
    };
  });
};

export const saveClasses = async (classes) => {
  const db = await initDB();
  const tx = db.transaction(CLASS_STORE, "readwrite");
  const store = tx.objectStore(CLASS_STORE);

  await store.clear();

  const reindexed = classes.map((cls, index) => ({
    ...cls,
    id: cls.name, // name is used as unique ID
    index,
    color: CLASS_COLORS[index % CLASS_COLORS.length],
  }));

  reindexed.forEach((cls) => store.put(cls));

  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
  });
};

export const editClassName = async (id, newName) => {
  const db = await initDB();
  const tx = db.transaction(CLASS_STORE, "readwrite");
  const store = tx.objectStore(CLASS_STORE);

  return new Promise((resolve, reject) => {
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const cls = getRequest.result;
      if (!cls) {
        reject(new Error("Class not found"));
        return;
      }

      cls.name = newName;

      const updateRequest = store.put(cls);
      updateRequest.onsuccess = () => resolve();
      updateRequest.onerror = () => reject(updateRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

export const deleteClass = async (id) => {
  const db = await initDB();
  const tx = db.transaction(CLASS_STORE, "readwrite");
  const store = tx.objectStore(CLASS_STORE);

  return new Promise((resolve, reject) => {
    const deleteRequest = store.delete(id);

    deleteRequest.onsuccess = () => resolve();
    deleteRequest.onerror = () => reject(deleteRequest.error);
  });
};

export const getClasses = async () => {
  const db = await initDB();
  const tx = db.transaction(CLASS_STORE, "readonly");
  const store = tx.objectStore(CLASS_STORE);
  const request = store.getAll();

  return new Promise((resolve) => {
    request.onsuccess = () => {
      resolve(request.result || []);
    };
  });
};

export const saveAnnotations = async (annotationList) => {
  const db = await initDB();
  const tx = db.transaction(ANNOTATION_STORE, "readwrite");
  const store = tx.objectStore(ANNOTATION_STORE);

  // Clear existing annotations
  await store.clear();

  // Save new annotations (store with id = 1)
  store.put({ id: 1, data: annotationList });

  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
  });
};

export const getAnnotations = async () => {
  const db = await initDB();
  const tx = db.transaction(ANNOTATION_STORE, "readonly");
  const store = tx.objectStore(ANNOTATION_STORE);
  const request = store.get(1);

  return new Promise((resolve) => {
    request.onsuccess = () => {
      resolve(request.result?.data || []);
    };
  });
};



export const saveMasterRectangles = async (rectangles, projectName) => {
  const key = `masterRects_${projectName}`;
  try {
    localStorage.setItem(key, JSON.stringify(rectangles));
  } catch (error) {
    console.error('Error saving rectangles to localStorage:', error);
    throw new Error('Failed to save rectangles');
  }
};

export const getMasterRectangles = async (projectName) => {
  const key = `masterRects_${projectName}`;
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Error reading rectangles from localStorage:', error);
    return [];
  }
};

export const saveSegmentAnnotations = async (segmentList) => {
  const db = await initDB();
  const tx = db.transaction(SEGMENT_STORE, "readwrite");
  const store = tx.objectStore(SEGMENT_STORE);

  // Clear existing segment annotations
  await store.clear();

  // Save new segment annotations (store with id = 1)
  store.put({ id: 1, data: segmentList });

  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
  });
};

export const getSegmentAnnotations = async () => {
  const db = await initDB();
  const tx = db.transaction(SEGMENT_STORE, "readonly");
  const store = tx.objectStore(SEGMENT_STORE);
  const request = store.get(1);

  return new Promise((resolve) => {
    request.onsuccess = () => {
      resolve(request.result?.data || []);
    };
  });
};

export const clearDB = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(
      [STORE_NAME, CLASS_STORE, PROJECT_STORE, ANNOTATION_STORE, SEGMENT_STORE],
      "readwrite"
    );
    tx.objectStore(STORE_NAME).clear();
    tx.objectStore(CLASS_STORE).clear();
    tx.objectStore(PROJECT_STORE).clear();
    tx.objectStore(ANNOTATION_STORE).clear();
    tx.objectStore(SEGMENT_STORE).clear();

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};
